import React from "react";
import NextImage, { ImageProps } from "next/image";

import { urlForImage } from "../sanity/utils";

// Kind of annoying to have to redefine this but can't find a good way to do it with sanity's generated types
export type ImageFromSanity = {
	asset?: {
		_ref: string;
		_type: "reference";
		_weak?: boolean;
	};
	extension: string | null;
	url: string | null;
};

type Props = {
	image: ImageFromSanity;
	priority?: boolean;
	fallBack?: React.ReactNode;
	width: number;
	height: number;
	dpr?: number;
	isFluid?: boolean;
} & Omit<ImageProps, "src" | "alt" | "width" | "height">;

const EXCLUDED_EXTENSIONS = ["svg", "gif"];

const SanityImage = ({
	image: source,
	fallBack,
	className,
	width,
	height,
	priority,
	fill,
	objectFit,
	dpr = 2,
	isFluid = false,
	placeholder = "empty",
	loading,
	sizes,
}: Props) => {
	if (!source) {
		return <>{fallBack}</>;
	}

	if (source.extension && EXCLUDED_EXTENSIONS.includes(source?.extension)) {
		return (
			<NextImage
				src={source.url ?? ""}
				width={width}
				height={height}
				alt="Sanity Image"
				className={className}
				priority={priority}
				placeholder={placeholder}
				loading={loading}
			/>
		);
	}

	const image = source?.asset?._ref;

	if (!image) {
		return <>{fallBack}</>;
	}

	const imageBuilder = urlForImage(source);

	if (!imageBuilder) {
		return <>{fallBack}</>;
	}

	const imageSrc = isFluid
		? imageBuilder.width(width).quality(100).dpr(dpr).url()
		: imageBuilder.height(height).width(width).quality(100).dpr(dpr).url();

	return (
		<NextImage
			width={!fill ? width : undefined}
			height={!fill ? height : undefined}
			className={className}
			src={imageSrc}
			alt="Sanity Image"
			priority={priority}
			fill={fill}
			objectFit={objectFit}
			placeholder={placeholder}
			loading={loading}
			quality={100}
			sizes={sizes}
		/>
	);
};

export default SanityImage;
